// AnagraficaView.jsx — amm-v3 (A2, ondata 29): la vista GENERICA "Anagrafiche"
// in React, flag-gated (shell_amm_v3_anagrafiche, default OFF) — UNA vista per
// le 8 pagine veicoli/polizze/documenti/dipendenti/atti/immobili/crediti/
// cantieri (il v1 le serve tutte con renderAnagrafica(el, key), r.4106-4267:
// il pilota DataTable indicato dalla spike o27 §2.3).
//
// Replica 1:1 la pagina v1: titolo e colonne DELEGATI ad AMM.anagConfig()
// (label VERBATIM per costruzione: i getter c.g(r) producono l'HTML v1, qui
// iniettato verbatim nelle celle come i badge del Cruscotto A1), tab
// Attivi(N)/Archiviati(N) sulle 3 pagine ARCHIVIABILI, filtro dipendenti,
// banner IMU sugli immobili (mai un "€ 0" bugiardo: appare solo con
// lookups.imuTotale valorizzato), bottoni riga (Registra utilizzo/Archivia/
// Riattiva/elimina) con la STESSA visibilità per ruolo (isAdmin) e stato.
//
// CONFINI (dichiarati): schede/editor/wizard = v1 (anagRowClick, editAnagrafica
// con lock B6 e 409+Ricarica, confirmDelete/confirmArchivia informate,
// apriRegistraUtilizzoCredito, openImportBuste); il refresh post-save del v1
// è renderPage(key) (r.4455) ⇒ passa dal wrap e ricarica QUESTA vista.
//
// QUIRK v1 REPLICATI (parità bug-compatibile):
//  - ogni redraw della pagina riparte dal tab Attivi e col filtro vuoto
//    (r.4109-4114: vistaArchiviati/filtro sono stato locale del render v1),
//    SALVO richiesta esplicita (riattiva e 'Vedi archiviati' restano/portano
//    sugli Archiviati — hint del factory, stessa semantica di apriArchiviati);
//  - il tab-switch NON rifà le fetch (le due liste sono caricate all'ingresso);
//  - nella vista Archiviati il click riga apre il form semplice (editAnagrafica),
//    non le schede dedicate (r.4195).
//
// DEVIAZIONI DICHIARATE (v3 non bug-compatibile qui):
//  D-A2.1 doppio click su elimina/Archivia/Riattiva = 1 richiesta (guardia ref
//         per riga; il v1 guarda solo Archivia/Riattiva, non elimina).
//  D-A2.2 durante il refresh le righe presenti restano visibili + banner
//         'Dati non aggiornati' con Riprova a ricarica fallita (D-A1.2/D-A1.5).
//  D-A2.3 tabella del design system (DataTable) invece della tabella v1
//         (intestazioni: stesse etichette di anagConfig, stili dt-*).
//  D-A2.5 il link 'Vedi archiviati' del toast è INERTE se nel frattempo si è
//         navigato altrove (il v1 ridipingerebbe la vista fantasma nel
//         container della pagina corrente).

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import { AM_NEW_TEXTS } from './api.js';
import {
  AN_TEXTS, AN_COLORS,
  emptyMessageAnag, nomeDipendente, mostraRegistraUtilizzo,
  readAnagrafica, riattivaAnag, refreshPagina, vaiATributi,
} from './anagrafiche-api.js';

// HTML prodotto dalle primitive v1 (già escapato da AMM.esc): iniettato verbatim
function V1Html({ html }) {
  if (!html) return null;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

export function AnagraficaView({ win, viewKey, tick, hintRef }) {
  const A = win.AMM;
  const cfg = A.anagConfig()[viewKey];
  const archiviabile = !!((A.ARCHIVIABILI || {})[viewKey]);
  const [dati, setDati] = React.useState({ loading: true, error: null, list: [], listArchiviati: [], lookups: {} });
  const [reloadN, setReloadN] = React.useState(0);
  const [vistaArchiviati, setVistaArchiviati] = React.useState(false);
  const [filtroDip, setFiltroDip] = React.useState('');
  const busyRows = React.useRef(new Set()); // guardia SINCRONA anti doppio-click per riga (D-A2.1)

  React.useEffect(() => {
    let vivo = true;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readAnagrafica(win, viewKey, archiviabile).then(
      (r) => {
        if (!vivo) return;
        setDati({ loading: false, error: null, ...r });
        // parità v1 r.4109-4114: ogni redraw riparte da Attivi e filtro vuoto,
        // salvo hint esplicito (riattiva / 'Vedi archiviati' → tab Archiviati)
        const hint = hintRef ? hintRef.current : null;
        if (hintRef) hintRef.current = null;
        setVistaArchiviati(hint === 'archiviati');
        setFiltroDip('');
      },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, viewKey, tick, reloadN]);

  const refresh = () => refreshPagina(win);

  const vediArchiviatiDaToast = () => {
    // D-A2.5: il link del toast agisce SOLO se questa vista è ancora al comando
    if (A.currentPage !== viewKey) return;
    if (hintRef) hintRef.current = 'archiviati';
    refresh();
  };

  const conGuardia = (chiave, fn) => {
    if (busyRows.current.has(chiave)) return;
    busyRows.current.add(chiave);
    Promise.resolve()
      .then(fn)
      .finally(() => busyRows.current.delete(chiave));
  };

  const onDelete = (r) => conGuardia('del:' + r.id, () => (
    A.confirmDeleteAnagrafica(viewKey, r.id, () => {
      A.toast(AN_TEXTS.toastEliminato); // parità r.4161
      refresh();
    }, nomeDipendente(viewKey, r))
  ));

  const onArchivia = (r) => conGuardia('arch:' + r.id, () => (
    A.confirmArchiviaAnagrafica(viewKey, r.id, () => {
      // parità r.4172-4174: toast con link 'Vedi archiviati' + si torna su Attivi
      A.toastConLink(AN_TEXTS.toastArchiviato, AN_TEXTS.vediArchiviati, vediArchiviatiDaToast);
      refresh();
    })
  ));

  const onRiattiva = (r) => conGuardia('riatt:' + r.id, async () => {
    try {
      await riattivaAnag(win, viewKey, r.id);
      A.toast(AN_TEXTS.toastRiattivato); // parità r.4183
      if (hintRef) hintRef.current = 'archiviati'; // parità r.4184: si resta sugli Archiviati
      refresh();
    } catch (e) {
      win.alert(AN_TEXTS.riattivaFallita + (e && e.message ? e.message : e)); // verbatim r.4185
    }
  });

  const onRegistraUtilizzo = (r) => A.apriRegistraUtilizzoCredito(r, () => refresh()); // parità r.4191

  const onRowClick = (r) => {
    // parità r.4193-4197: archiviati ⇒ form semplice; attivi ⇒ scheda dedicata
    if (vistaArchiviati) { A.editAnagrafica(viewKey, r || {}, dati.lookups); return; }
    A.anagRowClick(viewKey, r.id, r, dati.lookups);
  };

  const admin = A.isAdmin();
  const rowsBase = vistaArchiviati ? dati.listArchiviati : dati.list;
  const q = filtroDip.trim();
  const rows = q ? rowsBase.filter((r) => A.anagMatchesFiltro(viewKey, r, q)) : rowsBase;

  const columns = cfg.columns.map((c, i) => ({
    key: 'c' + i,
    label: <V1Html html={c.l} />,          // etichetta v1 (HTML, come nel th v1)
    render: (r) => <V1Html html={c.g(r)} />, // cella v1 (HTML del getter, verbatim)
  }));
  columns.push({
    key: '_azioni',
    label: '', // parità r.4130: th finale vuoto
    align: 'right',
    render: (r) => (
      <span className="whitespace-nowrap">
        {mostraRegistraUtilizzo(viewKey, r, vistaArchiviati) && (
          <button type="button" className="amm-cred-usa text-cyan-700 hover:text-cyan-900 text-xs font-medium mr-3" onClick={() => onRegistraUtilizzo(r)}>{AN_TEXTS.registraUtilizzo}</button>
        )}
        {admin && vistaArchiviati && (
          <button type="button" className="amm-anag-riattiva text-cyan-700 hover:text-cyan-900 text-xs font-medium" onClick={() => onRiattiva(r)}>{AN_TEXTS.riattiva}</button>
        )}
        {admin && !vistaArchiviati && archiviabile && (
          <button type="button" className="amm-anag-archivia text-slate-500 hover:text-slate-700 text-xs mr-3" onClick={() => onArchivia(r)}>{AN_TEXTS.archivia}</button>
        )}
        {admin && !vistaArchiviati && (
          <button type="button" className="amm-anag-del text-rose-600 hover:text-rose-800 text-xs" onClick={() => onDelete(r)}>{AN_TEXTS.elimina}</button>
        )}
      </span>
    ),
  });

  const tabStyle = (on) => (on
    ? { borderColor: AN_COLORS.tabOn, color: AN_COLORS.tabOn }
    : { borderColor: 'transparent', color: AN_COLORS.tabOff });

  return (
    <div data-amm-v3={viewKey}>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">
          {cfg.titolo}
          {viewKey === 'documenti' && <V1Html html={A.helpIcon('cu_sostitutiva') + A.helpIcon('archivio_anno_iniziale')} />}
        </h1>
        <div className="flex gap-2">
          {viewKey === 'dipendenti' && (
            <button id="amm-import-bp" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm px-3 py-2 rounded font-medium" onClick={() => A.openImportBuste(() => refresh())}>{AN_TEXTS.importaBuste}</button>
          )}
          {viewKey === 'cantieri' && (
            <button id="amm-cant-costi" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm px-3 py-2 rounded font-medium" onClick={() => A.renderPage('cantieri-costi')}>{AN_TEXTS.costiCantiere}</button>
          )}
          {!vistaArchiviati && (
            <button id="amm-anag-new" type="button" className="bg-cyan-700 hover:bg-cyan-600 text-white text-sm px-3 py-2 rounded font-medium" onClick={() => A.editAnagrafica(viewKey, {}, dati.lookups)}>{AN_TEXTS.btnNuovoPre + cfg.nuovo}</button>
          )}
        </div>
      </div>

      {viewKey === 'dipendenti' && (
        <input
          id="amm-anag-filtro"
          type="text"
          placeholder={AN_TEXTS.filtroDipPlaceholder}
          className="w-full max-w-sm border rounded px-3 py-1.5 text-sm mb-3"
          value={filtroDip}
          onChange={(ev) => setFiltroDip(ev.target.value)}
        />
      )}

      {viewKey === 'immobili' && dati.lookups && dati.lookups.imuTotale != null && (
        <div className="mb-3 px-3 py-2 rounded bg-cyan-50 border border-cyan-200 text-cyan-800 text-sm inline-flex items-center gap-2">
          <span>{AN_TEXTS.imuPre + String(dati.lookups.imuAnno) + ': '}<strong>{A.eur(dati.lookups.imuTotale)}</strong>{AN_TEXTS.imuCalcolata}</span>
          <span id="amm-imu-link" className="underline cursor-pointer text-cyan-700 hover:text-cyan-900" onClick={() => vaiATributi(win)}>{AN_TEXTS.imuLink}</span>
        </div>
      )}

      {archiviabile && (
        <div className="flex gap-4 border-b mb-3">
          <span id="amm-anag-tab-attivi" className="amm-anag-tab cursor-pointer text-sm font-medium pb-2 border-b-2" style={tabStyle(!vistaArchiviati)} onClick={() => setVistaArchiviati(false)}>{AN_TEXTS.tabAttiviPre + dati.list.length + ')'}</span>
          <span id="amm-anag-tab-archiviati" className="amm-anag-tab cursor-pointer text-sm font-medium pb-2 border-b-2" style={tabStyle(vistaArchiviati)} onClick={() => setVistaArchiviati(true)}>{AN_TEXTS.tabArchiviatiPre + dati.listArchiviati.length + ')'}</span>
        </div>
      )}

      {/* D-A2.2 (precedente D-A1.5): ricarica fallita con righe presenti ⇒
          l'errore si DICHIARA comunque, con Riprova accanto (mai silenzio) */}
      {dati.error && (dati.list.length > 0 || dati.listArchiviati.length > 0) && (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" className="dt-retry" style={{ marginTop: 0 }} onClick={() => setReloadN((n) => n + 1)}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      )}

      <div id="amm-anag-tbody">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey="id"
          loading={dati.loading}
          error={dati.error}
          onRetry={() => setReloadN((n) => n + 1)}
          emptyMessage={emptyMessageAnag(vistaArchiviati, dati.list.length > 0, cfg.nuovo)}
          onRowClick={onRowClick}
        />
      </div>
    </div>
  );
}

// Factory PER PAGINA (curried sulla key dal register): install.js la registra
// nel registry del wrap. hintRef porta la semantica 'apriArchiviati' del v1
// attraverso i refresh (riattiva / link del toast).
export function createAmmV3Anagrafica(win, viewKey) {
  let root = null;
  let mountedEl = null;
  let tick = 0;
  const hintRef = { current: null };

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0
      && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1; // ogni chiamata del wrapper = refresh dati (nav, rientro, post-azione)
    root.render(<AnagraficaView win={win} viewKey={viewKey} tick={tick} hintRef={hintRef} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
    hintRef.current = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
